import React, { useCallback } from "react"
import {
  IActivatable,
  IQualificationStepProps,
  ISharedContext,
  ViewMode,
  ViewModes,
  clearErrors,
  qualificationAnswered,
  qualificationMet,
} from "../support/registration_form_component"
import { YesNoQuestion } from "../support/yes_no_question"
import { AdminEditableText } from "../support/form_helpers"
import { QUALIFIED_STATUS_CHANGED } from "../wizard"
import { CommentsField } from "./claims"
import { IClientConfig } from "../clients/config"
import { YES, NO, YES_OR_NO, OTHER } from "../data"


export type QualificationStepProps = IQualificationStepProps & IActivatable & ISharedContext

export function QualificationStep({
  dispatch,
  setFormValue,
  active,
  form,
  config,
  client,
  urls,
  canEditText,
  showQualificationResult,
  view_mode,
}: QualificationStepProps) {
  const onQualifiedChange = useCallback((value: YES_OR_NO) => {
    dispatch({
      action: QUALIFIED_STATUS_CHANGED,
      qualified: value,
    })
  }, [dispatch]);

  const onCommentsChange = useCallback((value: string) => {
    setFormValue(["additional_contacts", "text"], value);
  }, [setFormValue]);

  const onReasonChange = useCallback((value: string) => {
    clearErrors();
    setFormValue(["eligibility", "reason_for_not_reporting"], value);
  }, [setFormValue]);

  const onQualificationDateChange = useCallback((value: string) => {
    setFormValue(["eligibility", "qualification_date"], value);
  }, [setFormValue]);

  if (!active) {
    return <div />;
  }

  const showCommentsField = qualificationAnswered(form) && !qualificationMet(form)

  let qualified: YES_OR_NO | null
  if (qualificationAnswered(form)) {
    qualified = qualificationMet(form) ? YES : NO
  } else {
    qualified = null
  }

  const needsReason = config.qualifierReasons && config.qualifierReasons.length > 0

  const selectedReason = form?.eligibility?.reason_for_not_reporting ?? ""

  return (
    <div className="step page-break-after">
      <h2 className="visible-print">
          Step 2: Qualification
      </h2>
      <div className="panel panel-default">
        <div className="panel-heading">
          <h1 className="panel-title">Qualifier</h1>
        </div>
        <div className="panel-body form-horizontal panel-body--md">
          <div className="col-sm-12">
            {!showQualificationResult ? (
              <InitialQuestion
                text={client.qualification_text}
                edit_text_url={urls.admin_edit_qualification_text}
                can_edit_text={canEditText}
                qualified={qualified}
                onChange={onQualifiedChange}
                view_mode={view_mode}
              />
            ) : (
              <div className="row qualification-confirmation-or-rejection">
                {qualificationMet(form) ? (
                  <AdminEditableText
                    text={client.qualification_confirmation_text}
                    url={urls.admin_edit_qualification_confirmation_text}
                    canEdit={canEditText}
                  />
                ) : (
                  <AdminEditableText
                    text={client.qualification_rejection_text}
                    url={urls.admin_edit_qualification_rejection_text}
                    canEdit={canEditText}
                  />
                )}
              </div>
            )}
          </div>
          {config.showQualificationDateField && qualified === YES && (
            <div className="col-sm-12">
              <QualificationDateField
                value={form?.eligibility?.qualification_date ?? ""}
                onChange={onQualificationDateChange}
                qualificationDateFieldLabel={config.qualificationDateFieldLabel}
              />
            </div>
          )}
        </div>
      </div>
      {qualified === "no" && needsReason && (
        <ReasonRadioButtons
          config={config}
          selected_reason={selectedReason}
          required={true}
          onChange={onReasonChange}
        />
      )}
      {showCommentsField &&
          <CommentsField
            value={form?.additional_contacts?.text ?? ""}
            onChange={onCommentsChange}
            required={needsReason && selectedReason === OTHER}
          />
      }
    </div>
  )
}


interface IInitialQuestionProps {
  text: string
  edit_text_url?: string | null
  can_edit_text: boolean
  qualified: YES_OR_NO | null
  view_mode: ViewMode
  onChange: (value: YES_OR_NO) => void
}
const InitialQuestion: React.FC<IInitialQuestionProps> = (props) => {
  return (
    <div>
      <div className="row qualification-text">
        <AdminEditableText
          text={props.text}
          url={props.edit_text_url}
          canEdit={props.can_edit_text}
        />
      </div>

      <div className="row">
        <YesNoQuestion
          text="Are you eligible under the above guidelines to submit?"
          name="eligibility_reporting"
          required={true}
          value={props.qualified}
          onChange={props.onChange}
        />
      </div>

      {props.view_mode === ViewModes.DOWNLOADABLE && <PrintModeExplanation />}
    </div>
  )
}


function QualificationDateField({
  value,
  onChange,
  qualificationDateFieldLabel,
}: {
  value: string
  onChange: (value: string) => void
  qualificationDateFieldLabel?: string | null
}) {
  // Default label doesn't mention the specific client.
  let label = qualificationDateFieldLabel
  if (!label) {
    label = "What was the earliest date that your organization met the threshold for qualifying to submit data?"
  }

  return (<>
    <div className="row">
      <hr/>
      <p>{label}</p>
    </div>
    <div className="form-group">
      <label htmlFor="eligibility_qualification_date" className="col-sm-3 control-label">Qualification Date</label>
      <div className="col-sm-3">
        <input
          type="date"
          className="form-control"
          name="eligibility[qualification_date]"
          id="eligibility_qualification_date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          max={"9999-12-31"}
          min={"1900-01-01"}
          data-fv-is-valid-date-message="Please enter a valid date."
        />
      </div>
    </div>
  </>)
}


interface IReasonRadioButtonsProps {
  config: IClientConfig
  selected_reason: string | null
  required?: boolean
  onChange: (value: string) => void
}
const ReasonRadioButtons: React.FC<IReasonRadioButtonsProps> = (props) => {
  const {
    config,
    required,
    selected_reason,
    onChange,
  } = props

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">
          <span className={required ? "required" : ""}>Reason for Not Reporting</span>
        </h1>
      </div>
      <div className="panel-body">
        <div className="form-group">
          <div className="help-block" data-errors-for="eligibility[reason_for_not_reporting]"></div>
          {(config.qualifierReasons || []).map((reason, index) => {
            return (
              <div className="radio" key={index}>
                {/* hardcode font size to same as body text above */}
                <label style={{fontSize: "1rem"}}>
                  <input
                    type="radio"
                    name="eligibility[reason_for_not_reporting]"
                    value={reason.value}
                    checked={selected_reason === reason.value}
                    onChange={(event) => onChange(event.currentTarget.value)}
                    required={required}
                    data-fv-notempty-message="Please select a reason:"
                    data-fv-form-group-selector=".form-group"
                  />
                  {reason.value === OTHER ? "Other (please specify)" : reason.value }
                </label>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}


function PrintModeExplanation() {
  return (
    <div className="row">
      <p>
        <strong>If you select &ldquo;No&rdquo; here, your registration will end immediately, with an opportunity to comment.</strong>
      </p>
    </div>
  )
}
